import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import ModalOverlay from 'components/ModalOverlay';
/* ─── DIPLOMA COMPONENT ─────────────────────────────────────────────────── */
/*
  Props:
  - open: boolean
  - onClose: fn
  - moduleTitle: string
  - userName: string
  - completedAt: string (ISO date) — opcional
  - diplomaConfig: object — configuración personalizada del admin (opcional)
*/

const DEFAULT_CONFIG = {
  bgMode: 'color',
  bgColor: '#faf7f0',
  gradColor1: '#faf7f0',
  gradColor2: '#e8e0d0',
  gradDir: '135deg',
  bgImage: null,
  textColor: '#2a1f0e',
  accentColor: '#b8975a',
  fontFamily: "'Playfair Display', serif",
  orgName: 'Plataforma de Formación',
  headline: 'Certificate of Completion',
  certText: 'Certifica que',
  completedText: 'ha completado satisfactoriamente el módulo',
  signerName: 'Dirección Académica',
  signerLabel: 'Firma autorizada',
  sealText: 'CERT✦VALID',
  sealImage: null,
};

const DIPLOMA_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .diploma-overlay {
    position: fixed; inset: 0; z-index: 99999;
    background: rgba(0,0,0,0.92);
    backdrop-filter: blur(12px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: diplOverlayIn 0.4s ease;
  }
  @keyframes diplOverlayIn { from { opacity:0; } to { opacity:1; } }

  .diploma-wrapper {
    position: relative;
    animation: diplWrapIn 0.5s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes diplWrapIn {
    from { opacity:0; transform: scale(0.85) translateY(20px); }
    to   { opacity:1; transform: scale(1)    translateY(0); }
  }

  .diploma-close {
    position: absolute; top: -14px; right: -14px; z-index: 10;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.7);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s;
    font-family: 'DM Sans', sans-serif;
  }
  .diploma-close:hover { background: rgba(255,255,255,0.2); color: #fff; }

  .diploma-cert {
    width: min(680px, 100%);
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 40px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05);
  }

  .diploma-cert::before, .diploma-cert::after {
    content: '';
    position: absolute;
    width: 80px; height: 80px;
    border: 3px solid var(--d-accent);
    z-index: 2;
  }
  .diploma-cert::before { top: 16px; left: 16px; border-right: none; border-bottom: none; }
  .diploma-cert::after  { bottom: 16px; right: 16px; border-left: none; border-top: none; }

  .diploma-inner {
    margin: 28px;
    border: 1.5px solid var(--d-accent);
    padding: 40px 48px 36px;
    position: relative;
    background: transparent;
  }

  .diploma-inner::before, .diploma-inner::after {
    content: '';
    position: absolute;
    width: 60px; height: 60px;
    border: 1.5px solid var(--d-accent);
    opacity: 0.5;
  }
  .diploma-inner::before { top: -1px; right: -1px; border-left: none; border-bottom: none; }
  .diploma-inner::after  { bottom: -1px; left: -1px; border-right: none; border-top: none; }

  .diploma-watermark {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none; z-index: 0;
    opacity: 0.04;
  }
  .diploma-watermark svg { width: 320px; height: 320px; }

  .diploma-content { position: relative; z-index: 1; text-align: center; }

  .diploma-org {
    font-family: 'DM Sans', sans-serif;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: var(--d-accent);
    margin-bottom: 6px;
  }

  .diploma-headline {
    font-size: 11px; font-style: italic;
    color: var(--d-text);
    opacity: 0.65;
    letter-spacing: 0.12em;
    margin-bottom: 20px;
  }

  .diploma-divider {
    display: flex; align-items: center; gap: 12px;
    margin: 16px auto;
    max-width: 360px;
  }
  .diploma-divider-line {
    flex: 1; height: 1px;
    background: linear-gradient(to right, transparent, var(--d-accent), transparent);
  }
  .diploma-divider-gem {
    width: 8px; height: 8px;
    background: var(--d-accent);
    transform: rotate(45deg);
    flex-shrink: 0;
  }

  .diploma-certifies {
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--d-text);
    opacity: 0.7;
    margin-bottom: 12px;
  }

  .diploma-name {
    font-size: 42px; font-weight: 700;
    color: var(--d-text);
    line-height: 1.1;
    margin: 0 0 6px;
  }

  .diploma-completed-text {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px; font-weight: 400;
    color: var(--d-text);
    opacity: 0.65;
    margin-bottom: 20px;
  }

  .diploma-module-title {
    font-size: 22px; font-weight: 700; font-style: italic;
    color: var(--d-accent);
    line-height: 1.3;
    padding: 0 20px;
  }

  .diploma-footer {
    display: flex; justify-content: space-between; align-items: flex-end;
    margin-top: 32px; padding-top: 20px;
    border-top: 1px solid var(--d-accent);
    opacity: 0.9;
  }

  .diploma-seal {
    width: 72px; height: 72px; flex-shrink: 0;
  }
  .diploma-seal-ring {
    width: 72px; height: 72px;
    border-radius: 50%;
    border: 2px solid var(--d-accent);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden;
    box-shadow: 0 0 0 4px rgba(0,0,0,0.06);
  }
  .diploma-seal-inner {
    font-size: 9px; font-weight: 900;
    color: var(--d-accent);
    text-align: center;
    letter-spacing: 0.05em;
    line-height: 1.3;
    text-transform: uppercase;
    padding: 4px;
  }
  .diploma-seal-ring img {
    width: 100%; height: 100%;
    object-fit: contain;
    padding: 6px;
  }

  .diploma-signature-block {
    text-align: center; flex: 1; padding: 0 20px;
  }
  .diploma-signature {
    font-size: 22px; font-style: italic;
    color: var(--d-text);
    margin-bottom: 4px;
    line-height: 1;
  }
  .diploma-sig-line {
    width: 140px; height: 1px;
    background: var(--d-accent);
    margin: 4px auto;
  }
  .diploma-sig-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 8px; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--d-text);
    opacity: 0.6;
  }

  .diploma-date-block { text-align: right; }
  .diploma-date-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 8px; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--d-text);
    opacity: 0.6;
    margin-bottom: 4px;
  }
  .diploma-date-value {
    font-size: 13px; color: var(--d-text);
  }

  @keyframes confettiFall {
    0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
    100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
  }
  .confetti-piece {
    position: fixed; top: -10px;
    width: 8px; height: 8px;
    border-radius: 2px;
    animation: confettiFall linear forwards;
    pointer-events: none; z-index: 100000;
  }

  .diploma-actions {
    display: flex; justify-content: center; gap: 12px;
    margin-top: 16px;
  }
  .diploma-action-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 10px 22px; border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; font-weight: 800;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: all 0.2s; border: none;
  }
  .diploma-btn-primary {
    background: linear-gradient(135deg, var(--d-accent), color-mix(in srgb, var(--d-accent) 70%, white));
    color: #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  }
  .diploma-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(0,0,0,0.35); }
  .diploma-btn-secondary {
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.15);
    color: rgba(255,255,255,0.7);
  }
  .diploma-btn-secondary:hover { background: rgba(255,255,255,0.12); color: #fff; }
`;

const Confetti = ({ accentColor = '#b8975a' }) => {
  const COLORS = [accentColor, '#ffffff', '#f5c842', '#2a1f0e', '#d4a94a'];
  const pieces = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    delay: `${Math.random() * 2}s`,
    duration: `${2.5 + Math.random() * 2}s`,
    size: `${6 + Math.random() * 6}px`,
    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
  }));
  return (
    <>
      {pieces.map(p => (
        <div key={p.id} className="confetti-piece" style={{
          left: p.left, background: p.color,
          animationDelay: p.delay, animationDuration: p.duration,
          width: p.size, height: p.size, borderRadius: p.borderRadius,
        }} />
      ))}
    </>
  );
};

const formatDate = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* Build CSS background from config */
const buildBackground = (cfg) => {
  if (cfg.bgMode === 'image' && cfg.bgImage) {
    return `url(${cfg.bgImage}) center/cover no-repeat`;
  }
  if (cfg.bgMode === 'gradient') {
    return `linear-gradient(${cfg.gradDir || '135deg'}, ${cfg.gradColor1 || '#faf7f0'}, ${cfg.gradColor2 || '#e8e0d0'})`;
  }
  return cfg.bgColor || '#faf7f0';
};

/* ══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════════════════════════════════ */
const ModuleDiploma = ({
  open,
  onClose,
  moduleTitle = '',
  userName = '',
  completedAt,
  diplomaConfig = null,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);

  // Merge default config with admin-provided config
  const cfg = { ...DEFAULT_CONFIG, ...(diplomaConfig || {}) };

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 3500);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const bgStyle = buildBackground(cfg);

  // CSS variables injected at cert level so all child elements inherit them
  const certVars = {
    '--d-text': cfg.textColor,
    '--d-accent': cfg.accentColor,
    background: bgStyle,
    fontFamily: cfg.fontFamily,
  };

  return (
    <>
      <style>{DIPLOMA_STYLES}</style>
      {showConfetti && <Confetti accentColor={cfg.accentColor} />}

      <ModalOverlay className="diploma-overlay" label={`Diploma: ${moduleTitle}`} onClose={onClose}>
        <div className="diploma-wrapper">

          <button type="button" className="diploma-close" onClick={onClose} aria-label="Cerrar diploma"><X size={16} /></button>

          <div className="diploma-cert" style={certVars}>

            <div className="diploma-watermark">
              <svg viewBox="0 0 200 200" fill="none">
                <circle cx="100" cy="100" r="90" stroke={cfg.accentColor} strokeWidth="2"/>
                <circle cx="100" cy="100" r="70" stroke={cfg.accentColor} strokeWidth="1"/>
                <path d="M100 20 L110 50 L140 50 L118 67 L126 97 L100 80 L74 97 L82 67 L60 50 L90 50 Z" fill={cfg.accentColor}/>
              </svg>
            </div>

            <div className="diploma-inner">
              <div className="diploma-content">

                <div className="diploma-org">{cfg.orgName}</div>
                <div className="diploma-headline" style={{ fontFamily: cfg.fontFamily }}>
                  {cfg.headline}
                </div>

                <div className="diploma-divider">
                  <div className="diploma-divider-line" />
                  <div className="diploma-divider-gem" />
                  <div className="diploma-divider-line" />
                </div>

                <div className="diploma-certifies">{cfg.certText}</div>

                <h2 className="diploma-name" style={{ fontFamily: cfg.fontFamily, color: cfg.textColor }}>
                  {userName || 'Empleado'}
                </h2>

                <p className="diploma-completed-text">{cfg.completedText}</p>

                <div className="diploma-module-title" style={{ fontFamily: cfg.fontFamily }}>
                  {moduleTitle}
                </div>

                <div className="diploma-divider" style={{ marginTop: 20 }}>
                  <div className="diploma-divider-line" />
                  <div className="diploma-divider-gem" />
                  <div className="diploma-divider-line" />
                </div>

                <div className="diploma-footer">
                  {/* Seal */}
                  <div className="diploma-seal">
                    <div className="diploma-seal-ring">
                      {cfg.sealImage ? (
                        <img src={cfg.sealImage} alt="Sello" />
                      ) : (
                        <div className="diploma-seal-inner">
                          {(cfg.sealText || 'CERT✦VALID').split('✦').map((part, i, arr) => (
                            <span key={i}>{part}{i < arr.length - 1 ? <><br/>✦<br/></> : ''}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signature */}
                  <div className="diploma-signature-block">
                    <div className="diploma-signature" style={{ fontFamily: cfg.fontFamily }}>
                      {cfg.signerName}
                    </div>
                    <div className="diploma-sig-line" />
                    <div className="diploma-sig-label">{cfg.signerLabel}</div>
                  </div>

                  {/* Date */}
                  <div className="diploma-date-block">
                    <div className="diploma-date-label">Fecha de emisión</div>
                    <div className="diploma-date-value" style={{ fontFamily: cfg.fontFamily }}>
                      {formatDate(completedAt)}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="diploma-actions">
            <button className="diploma-action-btn diploma-btn-primary" onClick={() => window.print()}>
              <Download size={13} /> Descargar diploma
            </button>
            <button className="diploma-action-btn diploma-btn-secondary" onClick={onClose}>
              Cerrar
            </button>
          </div>

        </div>
      </ModalOverlay>
    </>
  );
};

export default ModuleDiploma;